// src/client/components/LoanCard.tsx
import type { LoanFixture } from '@client/lib/fixtures';
import { formatLongDate, formatDueIn } from '@client/utils/dates';
import Card from './Card';
import Money from './Money';
import styles from './LoanCard.module.css';

const TONE: Partial<Record<LoanFixture['status'], string>> = {
  active: 'info',
  pending_approval: 'pending',
  delinquent: 'danger',
  default: 'danger',
  repaid: 'success',
};

const LABEL: Record<LoanFixture['status'], string> = {
  pending_approval: 'Pending approval',
  approved: 'Approved',
  disbursement_pending: 'Disbursing',
  active: 'Active',
  repaid: 'Repaid',
  delinquent: 'Overdue',
  default: 'Defaulted',
  restructured: 'Restructured',
  cancelled: 'Cancelled',
  disbursement_failed: 'Disbursement failed',
};

interface Props { loan: LoanFixture; }

export default function LoanCard({ loan }: Props) {
  const tone = TONE[loan.status] ?? 'muted';
  return (
    <Card>
      <div className={styles.head}>
        <div>
          <div className={styles.number}>{loan.loan_number}</div>
          <div className="small">{formatLongDate(loan.applied_at)}</div>
        </div>
        {/* The chip TEXT (e.g. "Active", "Overdue") is the WCAG 1.4.1
            "Use of Color" fulfillment. Don't switch to icon-only chips. */}
        <span className={[styles.chip, styles[tone]].filter(Boolean).join(' ')}>{LABEL[loan.status]}</span>
      </div>
      <div className={styles.amounts}>
        <div>
          <div className="eyebrow">Principal</div>
          <strong><Money amount={loan.principal_ngwee} decimals={0} /></strong>
        </div>
        <div>
          <div className="eyebrow">Outstanding</div>
          <strong><Money amount={loan.outstanding_ngwee} decimals={0} /></strong>
        </div>
      </div>
      {loan.due_at && loan.status !== 'repaid' && (
        <div className={styles.due} data-testid="due-line">{formatDueIn(loan.due_at)}</div>
      )}
    </Card>
  );
}
